import { getPostHogConfig, isPostHogEnabled } from "./posthogConfig";

let posthogInstance: any = null;

/**
 * Store the PostHog instance reference (called from provider after init)
 */
export const setPostHogInstance = (instance: any): void => {
	posthogInstance = instance;
};

/**
 * Get the PostHog instance — uses stored ref or falls back to dynamic import
 */
export const getPostHogInstance = async (): Promise<any> => {
	if (!isPostHogEnabled()) return null;
	if (posthogInstance) return posthogInstance;

	// Fallback: dynamically import if provider hasn't set it yet
	const posthogModule = await import("posthog-js");
	return posthogModule.default;
};

export interface PostHogEventParams {
	action: string;
	category?: string;
	label?: string;
	value?: string | number;
	[key: string]: unknown;
}

/**
 * Core event capture function — mirrors GA event structure.
 * Maps GA concepts → PostHog:
 *   action → event name
 *   category → event_category property
 *   label → event_label property
 *   value → value property
 *   additional props → spread into properties
 */
export const posthogEventEmitter = async ({
	action,
	category = "",
	label = "",
	value = "",
	...additionalProps
}: PostHogEventParams): Promise<void> => {
	if (!isPostHogEnabled() || !action) return;

	const ph = await getPostHogInstance();
	if (!ph) return;

	const eventProperties: Record<string, unknown> = {
		...(category && { event_category: category }),
		...(label && { event_label: label }),
		...(value !== "" && value !== undefined && { value }),
		...additionalProps,
		source: getPostHogConfig().source,
	};

	ph.capture(action, eventProperties);
};

/**
 * Identify a user in PostHog by email.
 * Skips if email is empty or numeric-only local part.
 */
export const identifyPostHogUser = async (
	email: string | undefined | null,
	properties?: Record<string, any>,
): Promise<void> => {
	const normalizedEmail = typeof email === "string" ? email.trim() : "";
	if (!normalizedEmail) return;

	// Skip numeric-only emails (e.g. "12345@x.com")
	const localPart = normalizedEmail.split("@")[0];
	if (/^\d+$/.test(localPart)) return;

	await posthogIdentify(normalizedEmail, {
		email: normalizedEmail,
		...properties,
	});
};

/**
 * Low-level identify call
 */
export const posthogIdentify = async (
	userId: string,
	userProperties?: Record<string, any>,
): Promise<void> => {
	if (!isPostHogEnabled()) return;

	const ph = await getPostHogInstance();
	if (!ph) return;

	ph.identify(userId, userProperties);
};

/**
 * Reset PostHog identity (call on logout)
 */
export const posthogReset = async (): Promise<void> => {
	if (!isPostHogEnabled()) return;
	const ph = await getPostHogInstance();
	if (!ph) return;
	ph.reset();
};

/**
 * Super properties — attached to every PostHog event via ph.register()
 */
export function getPostHogSuperProperties(): Record<
	string,
	string | number | boolean
> {
	const { source } = getPostHogConfig();
	return { source };
}

/**
 * Register super properties on the PostHog instance.
 * Called on init and on route changes.
 */
export async function registerPostHogSuperProperties(): Promise<void> {
	if (!isPostHogEnabled()) return;
	const ph = await getPostHogInstance();
	if (ph?.register) {
		ph.register(getPostHogSuperProperties());
	}
}
